import { useEffect, useState } from 'react';
import { Clock, ShieldAlert, X } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

/**
 * The client's way out of an ETA they cannot live with: switch this one project to an
 * editor who could start sooner. Framed as discouraged, never as an upgrade, and every
 * word of it comes from the server (app/editor_switch.py) so the copy the operator
 * approved is the copy that ships, and the acceptance record hashes the same text.
 *
 * No date is quoted before they accept: the server only says whether the offer is open
 * to them. On acceptance the real date is set and shown here.
 *
 * Shown only when the server says so: a dedicated editor with finished films, assigned
 * but not started, within 48 hours of the date being set or moved later, no song choices
 * open, not stopped by staff, and an eligible editor at least three days sooner.
 */
export default function EditorSwitchPanel({ project, onRefresh }) {
  const [offer, setOffer] = useState(undefined);      // undefined = not asked, null = not offered
  const [dialog, setDialog] = useState(false);
  const [ticked, setTicked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const windowOpen = project?.editor_switch?.window_open && !project?.editor_switch?.accepted_at && !project?.editor_switch?.stopped;

  useEffect(() => {
    if (!windowOpen) { setOffer(null); return; }
    let cancelled = false;
    api.get(`/projects/${project.id}/editor-switch`)
      .then(r => { if (!cancelled) setOffer(r.data?.offered ? r.data : null); })
      .catch(() => { if (!cancelled) setOffer(null); });
    return () => { cancelled = true; };
  }, [project?.id, windowOpen]);

  if (result) {
    return (
      <div className="mx-5 mb-4 rounded-xl border border-oxidized-teal/30 bg-oxidized-teal/[0.06] p-4" data-testid="editor-switch-done">
        <p className="text-[12px] text-zinc-100 font-medium">Your project has been switched.</p>
        <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
          Your new estimated delivery date is <strong className="text-zinc-100">{result.new_eta}</strong>, instead of {result.previous_eta}. We have emailed this to you as well.
        </p>
      </div>
    );
  }
  if (!offer) return null;
  const { panel, accept, expires_at, text_version } = offer;

  const confirm = async () => {
    if (!ticked) { toast.error('Please tick the confirmation first'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/projects/${project.id}/editor-switch/accept`, { text_version, confirmed: true });
      setResult(r.data); setDialog(false); onRefresh?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.requested) { setDialog(false); setOffer(null); toast.message(d.message, { duration: 12000 }); }
      else toast.error((typeof d === 'string' ? d : d?.message) || 'Could not switch right now');
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-5 mb-4 rounded-xl border border-ug-border bg-ug-surface/40 p-4" data-testid="editor-switch-panel">
      <h4 className="font-sans text-[12px] font-semibold text-zinc-100">{panel.title}</h4>
      {panel.body.map((t, i) => (
        <p key={i} className={`text-[11px] leading-relaxed mt-2 ${t.startsWith('The result may not feel') ? 'text-zinc-100 font-medium' : 'text-slate-400'}`}>{t}</p>
      ))}
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => setDialog(true)} data-testid="editor-switch-open"
          className="tap-auto text-[11px] font-medium rounded-md px-3 py-1.5 border border-ug-border text-slate-300 hover:text-zinc-100 hover:border-ug-border-strong transition-colors">
          {panel.button}
        </button>
        <button type="button" onClick={() => setOffer(null)} data-testid="editor-switch-wait"
          className="tap-auto text-[11px] font-semibold rounded-md px-3 py-1.5 bg-gold-leaf text-obsidian">
          {panel.wait_button}
        </button>
        <span className="text-[10px] text-slate-500 inline-flex items-center gap-1 ml-auto"><Clock className="w-3 h-3" /> {panel.footnote}</span>
      </div>

      {dialog && (
        <div className="ug-modal fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" role="dialog" aria-modal="true" data-testid="editor-switch-dialog">
          <div className="w-full max-w-md rounded-2xl bg-ug-raised border border-ug-border shadow-2xl">
            <div className="p-5 pb-4 border-b border-ug-border-subtle flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-heading text-xl text-zinc-100">{accept.title}</h3>
              </div>
              <button type="button" onClick={() => setDialog(false)} aria-label="Close" className="tap-auto p-1 rounded text-slate-500 hover:text-zinc-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-2.5">
              {accept.points.map((t, i) => (
                <p key={i} className={`text-[12px] leading-relaxed ${i === 0 ? 'text-zinc-100' : 'text-zinc-100/75'}`} data-testid={`editor-switch-point-${i}`}>{t}</p>
              ))}
              <label className="flex items-start gap-2 pt-2 text-[11px] text-zinc-100/85 leading-relaxed cursor-pointer">
                <input type="checkbox" checked={ticked} onChange={e => setTicked(e.target.checked)} data-testid="editor-switch-tick" className="mt-0.5" />
                <span>{accept.checkbox}</span>
              </label>
            </div>
            <div className="p-5 pt-3 border-t border-ug-border-subtle flex items-center gap-2">
              <button type="button" onClick={() => setDialog(false)} data-testid="editor-switch-cancel"
                className="tap-auto flex-1 py-2.5 rounded-lg bg-gold-leaf text-obsidian font-sans text-[11px] font-semibold uppercase tracking-wider">{accept.cancel}</button>
              <button type="button" onClick={confirm} disabled={!ticked || busy} data-testid="editor-switch-confirm"
                className="tap-auto flex-1 py-2.5 rounded-lg border border-ug-border text-slate-300 font-sans text-[11px] font-semibold uppercase tracking-wider disabled:opacity-40">{busy ? 'Switching…' : accept.confirm}</button>
            </div>
          </div>
        </div>
      )}
      {expires_at && <span className="sr-only" data-testid="editor-switch-expires">{expires_at}</span>}
    </div>
  );
}
